import * as React from 'react'
import {observer} from 'mobx-react-lite'
import {ColDef, IHeaderParams, NestedFieldPaths, SortDirection} from 'ag-grid-community'
import {Typography, Badge, Tooltip, IconButton} from '@mui/material'
import {makeOcStyles} from '@oc/styles'
import {IconByKey, ArrowThinIcon} from '@oc/icons'
import {lexCompare} from '@oc/utils'
import {cx} from '@emotion/css'

export interface OcAgGridSortTypeOption<TData, TValue = any> extends Pick<ColDef<TData, TValue>, 'comparator'> {
    sort: SortDirection
    sortIconVerticalAlign?: 'top' | 'bottom' | 'center'
}

export const OcAgGridNoneSortingTypeOption: OcAgGridSortTypeOption<any, any> = {
    sort: null
}

interface OcAgGridInitialSortProps extends Pick<ColDef, 'comparator'> {
    sort: SortDirection
}

export function ocAgGridGetColumnSortTypeOptionList<TData, TValue = any>(
    topField?: NestedFieldPaths<TData, TValue>,
    bottomField?: NestedFieldPaths<TData, TValue>,
    valueGetterFirst?: (data: TData) => string,
    valueGetterSecond?: (data: TData) => string,
    keepTwoLineAlignment = false
): OcAgGridSortTypeOption<TData, TValue>[] {
    const result = [{...OcAgGridNoneSortingTypeOption}]

    if (topField) {
        result.push(
            ocAgGridGetColumnSortTypeOption<TData, TValue>(
                topField,
                'asc',
                bottomField || keepTwoLineAlignment ? 'top' : 'center',
                valueGetterFirst
            )
        )
        result.push(
            ocAgGridGetColumnSortTypeOption<TData, TValue>(
                topField,
                'desc',
                bottomField || keepTwoLineAlignment ? 'top' : 'center',
                valueGetterFirst
            )
        )
    }

    if (bottomField) {
        result.push(ocAgGridGetColumnSortTypeOption<TData, TValue>(bottomField, 'asc', 'bottom', valueGetterSecond))
        result.push(ocAgGridGetColumnSortTypeOption<TData, TValue>(bottomField, 'desc', 'bottom', valueGetterSecond))
    }

    return result
}

export function ocAgGridGetColumnSortTypeOption<TData, TValue = any>(
    fieldName: NestedFieldPaths<TData, TValue>,
    sort: SortDirection,
    sortIconVerticalAlign: 'top' | 'center' | 'bottom',
    valueGetter?: (data: TData) => string,
    lexCompareOptions?: Intl.CollatorOptions | undefined
): OcAgGridSortTypeOption<TData, TValue> {
    return {
        sort: sort,
        comparator: (_, __, a, b) => {
            if (!a.data || !b.data) {
                return 0
            }
            let valueA = ''
            let valueB = ''

            if (valueGetter) {
                valueA = (valueGetter(a.data) || '').toLowerCase()
                valueB = (valueGetter(b.data) || '').toLowerCase()
            } else {
                const paths = fieldName.split('.')
                valueA =
                    paths
                        .reduce((acc, cur) => (acc as any)?.[cur], a.data)
                        ?.toString()
                        ?.toLowerCase() ?? ''
                valueB =
                    paths
                        .reduce((acc, cur) => (acc as any)?.[cur], b.data)
                        ?.toString()
                        ?.toLowerCase() ?? ''
            }

            return lexCompare(valueA, valueB, undefined, lexCompareOptions || {numeric: true})
        },
        sortIconVerticalAlign: sortIconVerticalAlign
    }
}

export interface OcAgGridCustomHeaderFrameworkBaseProps<TData, TValue = any> {
    firstLine?: {
        label?: string
        highlight?: boolean
        color?: string
        keepEmptySpace?: boolean
        tooltip?: string
        customComponent?: JSX.Element
    }
    secondLine?: {
        label?: string
        highlight?: boolean
        color?: string
        keepEmptySpace?: boolean
        tooltip?: string
        customComponent?: JSX.Element
    }
    sortOrderOptions?: OcAgGridSortTypeOption<TData, TValue>[]
    rootPadding?: string
}

export type OcAgGridCustomHeaderFrameworkProps<TData, TValue = any> = IHeaderParams<TData> &
    OcAgGridCustomHeaderFrameworkBaseProps<TData, TValue>

const useStyles = makeOcStyles(theme => ({
    rootDiv: {
        display: 'flex',
        height: '100%',
        alignContent: 'center',
        width: '100%',
        justifyContent: 'space-between',
        flexWrap: 'nowrap'
    },
    textColor: {
        color: theme.syskronPalette.common.textGrey
    },
    highlightedHeader: {
        fontWeight: theme.typography.fontWeightBold?.valueOf() as number
    },
    iconBase: {
        marginLeft: '5px'
    },
    alignTop: {
        alignSelf: 'flex-start'
    },
    alignBottom: {
        alignSelf: 'flex-end'
    },
    alignCenter: {
        alignSelf: 'center'
    },
    fontSize: {
        fontSize: '12px'
    },
    flex: {
        display: 'flex'
    },
    flexColumn: {
        flexDirection: 'column'
    },
    filterIcon: {
        alignSelf: 'center',
        marginLeft: theme.spacing(1)
    },
    filterIconColor: {
        color: theme.syskronPalette.common.textMediumGrey
    }
}))

export function toSortType(sort: string | null | undefined): SortDirection {
    switch (sort) {
        case 'asc':
        case 'desc':
            return sort
        default:
            return null
    }
}

const _OcAgGridCustomHeaderFramework = <TData, TValue = any>(
    props: OcAgGridCustomHeaderFrameworkProps<TData, TValue>
) => {
    const {classes, cx} = useStyles()
    const {firstLine, secondLine, sortOrderOptions, setSort, column, api, showColumnMenu, rootPadding} = props
    const [columnState, setColumnState] = React.useState(
        api.getColumnState().find(state => state.colId === column.getColId())
    )
    const [colDef, setColDef] = React.useState(() => column.getColDef())
    const [sortIndex, setSortIndex] = React.useState(() => column.getSortIndex())

    const [multiSortActive, setMultiSortActive] = React.useState(
        api.getColumnState().some(item => item.colId !== column.getColId() && item.sort)
    )

    const [isFilterActive, setIsFilterActive] = React.useState(column.isFilterActive())

    const displayFirst = firstLine && (firstLine.keepEmptySpace !== false || firstLine.label)
    const displaySecond = secondLine && (secondLine.keepEmptySpace !== false || secondLine.label)

    const comparator = React.useMemo(() => colDef.comparator, [colDef])

    const filterButtonRef = React.useRef(null)

    const filterOnClick = React.useCallback(
        (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
            event.stopPropagation()
            if (filterButtonRef.current !== null && typeof showColumnMenu === 'function') {
                showColumnMenu(filterButtonRef.current)
            }
        },
        [showColumnMenu, filterButtonRef]
    )

    const setIsFilterActiveCallback = React.useCallback(() => {
        setIsFilterActive(column.isFilterActive())
    }, [setIsFilterActive, column])

    React.useEffect(() => {
        column?.addEventListener('filterChanged', setIsFilterActiveCallback)
        return () => column.removeEventListener('filterChanged', setIsFilterActiveCallback)
    }, [column, setIsFilterActiveCallback])

    const selectedSortOption = React.useMemo(() => {
        return sortOrderOptions?.find(sort => {
            const formatted = toSortType(columnState?.sort)
            if (sort.sort === formatted && sort.comparator === comparator) {
                return true
            }
            if (sort.sort === formatted && formatted === null) {
                return true
            }
            return false
        })
    }, [comparator, sortOrderOptions, columnState?.sort])

    const classNameFirst = cx(
        {
            [classes.textColor]: typeof firstLine?.color === 'undefined',
            [classes.highlightedHeader]: firstLine?.highlight
        },
        classes.fontSize
    )

    const classNameSecond = cx(
        {
            [classes.textColor]: typeof secondLine?.color === 'undefined',
            [classes.highlightedHeader]: secondLine?.highlight
        },
        classes.fontSize
    )

    const iconClassName = cx({
        [classes.iconBase]: true,
        [classes.alignTop]: selectedSortOption?.sortIconVerticalAlign === 'top',
        [classes.alignBottom]: selectedSortOption?.sortIconVerticalAlign === 'bottom',
        [classes.alignCenter]:
            selectedSortOption?.sortIconVerticalAlign === 'center' ||
            typeof selectedSortOption?.sortIconVerticalAlign === 'undefined'
    })

    const sortOnChange = React.useCallback(
        (multiSort: boolean) => {
            if (typeof sortOrderOptions !== 'undefined' && sortOrderOptions.length > 0) {
                let refreshNeeded = false
                // check if other columns use sorting and should be updated too - if redraw is needed
                if (api.getColumnState().some(item => item.colId !== column.getColId() && item.sort)) {
                    refreshNeeded = true
                }
                setMultiSortActive(refreshNeeded)

                // find new order type and change grid settings
                if (
                    typeof selectedSortOption !== 'undefined' &&
                    sortOrderOptions.length > sortOrderOptions.indexOf(selectedSortOption) + 1
                ) {
                    const newSort = sortOrderOptions[sortOrderOptions.indexOf(selectedSortOption) + 1]
                    column.getColDef().comparator = newSort.comparator // TODO otestovat, protoze to nejspis nefunguje
                    //column.setColDef({...column.getColDef(), comparator: newSort.comparator}, {})
                    setSort(newSort.sort, multiSort)
                } else {
                    const newSort = sortOrderOptions[0]
                    column.getColDef().comparator = newSort.comparator // TODO otestovat, protoze to nejspis nefunguje
                    //column.setColDef({...column.getColDef(), comparator: newSort.comparator}, {})
                    setSort(newSort.sort, multiSort)
                }

                // force refresh - via state or redraw all
                if (refreshNeeded) {
                    api.refreshHeader()
                } else {
                    setColumnState(api.getColumnState().find(state => state.colId === column.getColId()))
                    setColDef(column.getColDef())
                    setSortIndex(column.getSortIndex())
                }
            }
        },
        [sortOrderOptions, api, column, setSort, selectedSortOption]
    )

    return (
        <div onClick={e => sortOnChange(e.ctrlKey)} className={classes.rootDiv} style={{padding: rootPadding || '8px'}}>
            <div className={classes.flex}>
                <div className={cx(classes.flex, classes.alignCenter, classes.flexColumn)}>
                    {displayFirst &&
                        (firstLine.customComponent ? (
                            firstLine.customComponent
                        ) : (
                            <Tooltip title={firstLine.tooltip || ''}>
                                <Typography
                                    className={classNameFirst}
                                    style={typeof firstLine.color !== 'undefined' ? {color: firstLine.color} : {}}
                                >
                                    {typeof firstLine.label !== 'undefined' && firstLine.label !== ''
                                        ? firstLine.label
                                        : '\u00A0'}
                                </Typography>
                            </Tooltip>
                        ))}

                    {displaySecond &&
                        (secondLine.customComponent ? (
                            secondLine.customComponent
                        ) : (
                            <Tooltip title={secondLine.tooltip || ''}>
                                <Typography
                                    className={classNameSecond}
                                    style={typeof secondLine.color !== 'undefined' ? {color: secondLine.color} : {}}
                                >
                                    {typeof secondLine.label !== 'undefined' && secondLine.label !== ''
                                        ? secondLine.label
                                        : '\u00A0'}
                                </Typography>
                            </Tooltip>
                        ))}
                </div>

                {typeof selectedSortOption !== 'undefined' && selectedSortOption.sort !== null ? (
                    <Badge
                        badgeContent={multiSortActive ? ((sortIndex || 0) + 1)?.toString() : ''}
                        className={iconClassName}
                    >
                        <ArrowThinIcon size="16px" rotate={selectedSortOption.sort === 'desc' ? 'down' : undefined} />
                    </Badge>
                ) : null}

                {isFilterActive ? (
                    <IconByKey
                        iconKey="maintenance_filter-list"
                        className={cx(classes.textColor, classes.filterIcon)}
                        size="16px"
                    />
                ) : null}
            </div>
            {props.enableMenu || !!colDef?.filter ? (
                <IconButton onClick={filterOnClick} ref={filterButtonRef} size="large">
                    <IconByKey iconKey="maintenance_filter-list" size="16px" className={classes.filterIconColor} />
                </IconButton>
            ) : null}
        </div>
    )
}

export const OcAgGridCustomHeaderFramework = observer(_OcAgGridCustomHeaderFramework)

export function getOcAgGridCustomHeaderFrameworkColDefs<TData, TValue = any>(
    props: OcAgGridCustomHeaderFrameworkBaseProps<TData, TValue>,
    allowFilterButton?: boolean,
    headerClassBase?: string,
    initialSort?: OcAgGridInitialSortProps
): Pick<
    ColDef<TData, TValue>,
    'headerComponent' | 'headerComponentParams' | 'filter' | 'headerClass' | 'initialSort' | 'comparator'
> {
    const className = cx(headerClassBase, {
        'oc-header-with-filter': allowFilterButton
    })

    return {
        headerComponent: OcAgGridCustomHeaderFramework,
        headerComponentParams: props,
        filter: !!allowFilterButton,
        headerClass: className,
        initialSort: initialSort?.sort,
        comparator: initialSort?.comparator
    }
}
